import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, ExternalLink, FileText, FileWarning, Loader2,
  CheckCircle2, XCircle,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";
// import { CKEditor } from "@ckeditor/ckedito

import { useNavigate } from "react-router-dom";
const SEQUENCES = Array.from({ length: 31 }, (_, i) => i + 1);

const emptyForm = {
  menu_pageid: "",
  parent_pageid: "", // submenu id
  page_name: "",
  seq_id: "",
  page_title: "",
  page_contents: "",
  seo_metatag: "",
  seo_metatitle: "",
  seo_metadesc: "",
  seo_keyword: "",
  ischeckedpdf: false,
};

// function attachmentUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/menudocuments/${filename}`;
// }

function attachmentUrl(filename) {
  if (!filename) return null;

  const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/menudocuments/${fileName}`;
}

// Groups consecutive rows into Menu -> Submenu -> [items], matching the original
// PHP's 3-level nested display exactly.
function groupByMenuThenSubmenu(items) {
  const groups = [];
  let menuGroup = null;
  let subGroup = null;
  for (const item of items) {
    if (!menuGroup || menuGroup.menu_pageid !== item.menu_pageid) {
      menuGroup = { menu_pageid: item.menu_pageid, menu_name: item.menu_name, submenus: [] };
      groups.push(menuGroup);
      subGroup = null;
    }
    if (!subGroup || subGroup.parent_pageid !== item.parent_pageid) {
      subGroup = { parent_pageid: item.parent_pageid, submenu_name: item.submenu_name, rows: [] };
      menuGroup.submenus.push(subGroup);
    }
    subGroup.rows.push(item);
  }
  return groups;
}

export default function ManageSubSubMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [menus, setMenus] = useState([]);
  const [submenus, setSubmenus] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchMenus = async () => {
    try {
      const res = await adminApi.get("/admin/sub-sub-menu/meta/menus");
      setMenus(res.data.menus || []);
    } catch (err) {
      console.error("Could not load menu list:", err);
    }
  };

  const fetchSubmenusFor = async (menuPageId) => {
    if (!menuPageId) return setSubmenus([]);
    try {
      const res = await adminApi.get("/admin/sub-sub-menu/meta/submenus", { params: { parent: menuPageId } });
      setSubmenus(res.data.submenus || []);
    } catch (err) {
      console.error("Could not load submenu list:", err);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/sub-sub-menu");
      setItems(res.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load sub-sub menu contents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
    fetchItems();
  }, []);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => setSelected(selected.length === items.length ? [] : items.map((i) => i.page_id));
  const toggleSelectOne = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ── Status toggle (1 = Active, 2 = Inactive) ────────────────────────────
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();
    const turningOn = item.page_status !== 1;
    const ok = window.confirm(`Are you sure you want to change the status to ${turningOn ? "Active" : "Inactive"}?`);
    if (!ok) return;
    try {
      const res = await adminApi.patch(`/admin/sub-sub-menu/${item.page_id}/status`);
      setItems((prev) =>
        prev.map((i) => (i.page_id === item.page_id ? { ...i, page_status: res.data.page_status } : i))
      );
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not update status.");
    }
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(`Are you sure you want to delete ${selected.length} selected record(s)? This cannot be undone.`);
    if (!ok) return;
    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/sub-sub-menu/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} record(s) deleted.`);
      setSelected([]);
      fetchItems();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected records.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSubmenus([]);
    setFile(null);
    setFilePreviewName(null);
    setExistingAttachment(null);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = async (item) => {
    setEditingId(item.page_id);
    setFile(null);
    setFilePreviewName(null);
    setFormError("");
    setShowModal(true);
    try {
      const res = await adminApi.get(`/admin/sub-sub-menu/${item.page_id}`);
      const d = res.data.item;
      await fetchSubmenusFor(d.menu_pageid);
      setForm({
        menu_pageid: d.menu_pageid || "",
        parent_pageid: d.parent_pageid || "",
        page_name: d.page_name || "",
        seq_id: d.seq_id || "",
        page_title: d.page_title || "",
        page_contents: d.page_contents || "",
        seo_metatag: d.seo?.seo_metatag || "",
        seo_metatitle: d.seo?.seo_metatitle || "",
        seo_metadesc: d.seo?.seo_metadesc || "",
        seo_keyword: d.seo?.seo_keyword || "",
        ischeckedpdf: !!d.ischeckedpdf,
      });
      setExistingAttachment(d.attachment);
    } catch (err) {
      setFormError(err.response?.data?.message || "Could not load this record.");
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleMenuChange = (menuPageId) => {
    setForm((prev) => ({ ...prev, menu_pageid: menuPageId, parent_pageid: "" }));
    fetchSubmenusFor(menuPageId);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setFormError("Only PDF files are allowed.");
      return;
    }
    setFormError("");
    setFile(f);
    setFilePreviewName(f.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.menu_pageid) return setFormError("Menu Name is required.");
    if (!form.parent_pageid) return setFormError("Sub Menu Name is required.");
    if (!form.page_name.trim()) return setFormError("Sub-sub Menu Name is required.");
    if (!form.seq_id) return setFormError("Sequence is required.");
    if (form.ischeckedpdf && !file && !existingAttachment) {
      return setFormError("A PDF file is required when 'Add PDF for this menu' is checked.");
    }

    const fd = new FormData();
    fd.append("parent_pageid", form.parent_pageid);
    fd.append("page_name", form.page_name.trim());
    fd.append("seq_id", form.seq_id);
    fd.append("page_title", form.page_title.trim());
    fd.append("page_contents", form.page_contents.trim());
    fd.append("seo_metatag", form.seo_metatag);
    fd.append("seo_metatitle", form.seo_metatitle);
    fd.append("seo_metadesc", form.seo_metadesc);
    fd.append("seo_keyword", form.seo_keyword);
    fd.append("ischeckedpdf", form.ischeckedpdf ? "1" : "0");
    if (file) fd.append("attachment", file);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/sub-sub-menu/${editingId}`, fd);
        showToast("success", "Sub-sub menu content updated.");
      } else {
        await adminApi.post("/admin/sub-sub-menu", fd);
        showToast("success", "Sub-sub menu content added.");
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = items.length > 0 && selected.length === items.length;
  const groups = groupByMenuThenSubmenu(items);
const navigate = useNavigate();
  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Sub-sub Menu Contents
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete} disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          {/* <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} /> Add New
          </button> */}
          <button
  onClick={() =>
    navigate("/admin/contents/sub-sub-menu/add")
  }
  className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
>
  <Plus size={15} />
  Add New
</button>
        </div>
      </div>

      {/* Table — grouped Menu -> Submenu -> rows, same as the original PHP */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No records yet — click "Add New" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                  <tr className="text-left text-base">
                    <th className="th-colorful px-3 py-3  w-8 border-b-2 border-r normal-case"  >
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer" />
                    </th>
                    <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r"  >Sub-sub Menu Name</th>
                    <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r"style={{textAlign:"center"}} >Attachment</th>
                    <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Sequence</th>
                    <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Is Content?</th>
                    <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2">Status</th>
                  </tr>
                </thead>
              <tbody>
                {groups.map((menuGroup) => (
                  <React.Fragment key={menuGroup.menu_pageid}>
                    <tr className="bg-slate-100/70">
                      <td colSpan={6} className="px-5 py-1.5 text-xs font-semibold text-[#0B2545] border-b border-slate-200">
                        {menuGroup.menu_name}
                      </td>
                    </tr>
                    {menuGroup.submenus.map((subGroup) => (
                      <React.Fragment key={subGroup.parent_pageid}>
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="px-5 py-1 text-xs font-medium text-slate-500 border-b border-slate-200">
                            --- {subGroup.submenu_name}
                          </td>
                        </tr>
                        <AnimatePresence initial={false}>
                          {subGroup.rows.map((it, i) => (
                            <motion.tr
                              key={it.page_id} layout
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              transition={{ duration: 0.2, delay: i * 0.02 }}
                              // onClick={() => openEditModal(it)}
                              // onClick={() =>
                              //     navigate(
                              //       `/admin/contents/sub-sub-menu/edit/${it.page_id}`
                              //     )
                              //   }

                              onClick={() =>
                                navigate("/admin/contents/sub-sub-menu/edit", {
                                  state: {
                                    page_id: it.page_id
                                  }
                                })
                              }
                              className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                                  selected.includes(it.page_id) ? "bg-[#0EA5A4]/15" : ""
                                }`}
                            >
                              <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={selected.includes(it.page_id)} onChange={() => toggleSelectOne(it.page_id)}
                                  className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer" />
                              </td>
                              <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">{it.page_name}</td>
                              <td className="px-3 py-2 border-r border-slate-200" style={{textAlign:"center"}}>
                                {it.attachment ? (
                                  <a href={attachmentUrl(it.attachment)} target="_blank" rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline">
                                    {/* <FileText size={14} className="shrink-0" /> View <ExternalLink size={11} className="shrink-0" /> */}
                                    <div style={{border:"0.5px solid #999",padding:"7px",marginTop:"3px",marginBottom:"3px",borderRadius:"7px"}}><i style={{color:"green"}} class="fa fa-eye" aria-hidden="true"></i></div>
                                  </a>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs"><FileWarning size={14} /> None</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{it.seq_id}</td>
                              <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{it.ischecked ? "Yes" : "No"}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center">
                                  <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={(e) => handleToggleStatus(it, e)}
                                    title={it.page_status === 1 ? "Active — click to deactivate" : "Inactive — click to activate"}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                                      it.page_status === 1
                                        ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                        : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                                    }`}
                                  >
                                    {it.page_status === 1 ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                                    {it.page_status === 1 ? "Active" : "Inactive"}
                                  </motion.button>
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </AnimatePresence>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Sub-sub Menu Content" : "Add Sub-sub Menu Content"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
              </div>

              <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 space-y-5">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Menu Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    value={form.menu_pageid} onChange={(e) => handleMenuChange(e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {menus.map((m) => <option key={m.page_id} value={m.page_id}>{m.page_name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Sub Menu Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    value={form.parent_pageid}
                    onChange={(e) => setForm({ ...form, parent_pageid: e.target.value })}
                    disabled={!form.menu_pageid}
                  >
                    <option value="">-- Select --</option>
                    {submenus.map((s) => <option key={s.page_id} value={s.page_id}>{s.page_name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Sub-sub Menu Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    value={form.page_name} onChange={(e) => setForm({ ...form, page_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Sequence <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    value={form.seq_id} onChange={(e) => setForm({ ...form, seq_id: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {SEQUENCES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Title</label>
                  <input
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    value={form.page_title} onChange={(e) => setForm({ ...form, page_title: e.target.value })}
                  />
                </div>
                {/* <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Contents</label>
                  <textarea rows={5}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                    value={form.page_contents} onChange={(e) => setForm({ ...form, page_contents: e.target.value })} />
                  <p className="text-xs text-slate-400 mt-1">Leaving this empty marks "Is Content?" as No.</p>
                </div> */}
                 <div className="border border-slate-200 rounded-lg overflow-hidden">
                {/* <CKEditor
                  editor={ClassicEditor}
                  data={form.page_contents || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setForm((prev) => ({
                      ...prev,
                      page_contents: data,
                    }));
                  }}
                /> */}
              </div>

                <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] pt-1">SEO Details</h5>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Meta Tags</label>
                  <textarea rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                    value={form.seo_metatag} onChange={(e) => setForm({ ...form, seo_metatag: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Meta Title</label>
                  <textarea rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                    value={form.seo_metatitle} onChange={(e) => setForm({ ...form, seo_metatitle: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Meta Description</label>
                  <textarea rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                    value={form.seo_metadesc} onChange={(e) => setForm({ ...form, seo_metadesc: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Keyword</label>
                  <textarea rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                    value={form.seo_keyword} onChange={(e) => setForm({ ...form, seo_keyword: e.target.value })} />
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.ischeckedpdf}
                    onChange={(e) => setForm({ ...form, ischeckedpdf: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                  <span className="text-sm font-medium text-slate-700">Add PDF for this menu</span>
                </label>

                <AnimatePresence initial={false}>
                  {form.ischeckedpdf && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }} className="overflow-hidden"
                    >
                      <div className="bg-slate-50 rounded-lg p-4">
                        <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                          File Upload (PDF only, max 4MB) <span className="text-red-500">*</span>
                        </label>
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition bg-white"
                        >
                          <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                            <FileText size={18} className="text-slate-300" />
                          </div>
                          <div className="text-xs text-slate-500">
                            {filePreviewName ? filePreviewName : existingAttachment ? "Click to replace attachment" : "Click to choose a PDF file"}
                          </div>
                          <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleFileChange} className="hidden" />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{formError}</p>
                )}

                <div className="flex items-center gap-3 pt-2 pb-1 sticky bottom-0 bg-white">
                  <button
                    type="submit" disabled={saving}
                    className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg px-6 py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={15} />}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button type="button" onClick={closeModal} className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
